import {useEffect, useRef, useCallback} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {DownloadStateTypes, updateDisplayProgressNew, type OrderDownloadStateNew} from '@Components/download-panel/download-panel.reducer';

const DURATION_PHASE_1 = 90000;
const DURATION_PHASE_2 = 405000;
const COMPLETION_DURATION = 400;

export const useDownloadProgressAnimation = (): void => {
  const dispatch = useAppDispatch();
  const orderStateHashmapNew = useAppSelector((state) => state.downloadDialog.orderStateHashmapNew);
  const currentlyProcessingOrderIdKeys = useAppSelector((state) => state.downloadDialog.currentlyProcessingOrderIdKeys);

  const animationFramesRef = useRef<Record<string, number>>({});
  const animatingOrdersRef = useRef<Set<string>>(new Set());

  const latestStateRef = useRef<Record<string, OrderDownloadStateNew>>(orderStateHashmapNew);
  latestStateRef.current = orderStateHashmapNew;

  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const completingOrdersRef = useRef<Set<string>>(new Set());

  const animateToCompletionInternal = (orderIdKey: string, startProgress: number): void => {
    if (completingOrdersRef.current.has(orderIdKey)) {
      return;
    }

    completingOrdersRef.current.add(orderIdKey);

    if (animationFramesRef.current[orderIdKey]) {
      cancelAnimationFrame(animationFramesRef.current[orderIdKey]);
      delete animationFramesRef.current[orderIdKey];
    }

    let startTime: number | null = null;

    const animate = (timestamp: number): void => {
      startTime ??= timestamp;

      const elapsed = timestamp - startTime;
      const progressFraction = Math.min(elapsed / COMPLETION_DURATION, 1);
      const newProgress = startProgress + (100 - startProgress) * progressFraction;

      dispatchRef.current(updateDisplayProgressNew({orderIdKey, displayProgress: newProgress}));

      if (progressFraction < 1) {
        animationFramesRef.current[orderIdKey] = requestAnimationFrame(animate);
      } else {
        delete animationFramesRef.current[orderIdKey];
        animatingOrdersRef.current.delete(orderIdKey);
        completingOrdersRef.current.delete(orderIdKey);
      }
    };

    animationFramesRef.current[orderIdKey] = requestAnimationFrame(animate);
  };

  const startAnimation = useCallback((orderIdKey: string, initialProgress: number): void => {
    if (animatingOrdersRef.current.has(orderIdKey)) {
      return;
    }

    animatingOrdersRef.current.add(orderIdKey);

    const totalDuration = DURATION_PHASE_1 + DURATION_PHASE_2;

    let calculatedElapsed = 0;
    if (initialProgress > 0 && initialProgress < 90) {
      calculatedElapsed = (initialProgress / 90) * DURATION_PHASE_1;
    } else if (initialProgress >= 90 && initialProgress < 99) {
      calculatedElapsed = DURATION_PHASE_1 + ((initialProgress - 90) / 9) * DURATION_PHASE_2;
    }

    let startTime: number | null = null;

    const animate = (timestamp: number): void => {
      const order = latestStateRef.current[orderIdKey];

      if (!order) {
        animatingOrdersRef.current.delete(orderIdKey);
        delete animationFramesRef.current[orderIdKey];
        return;
      }

      if (order.state === DownloadStateTypes.READY || order.state === DownloadStateTypes.ERROR) {
        delete animationFramesRef.current[orderIdKey];
        const currentProgress = order.displayProgress ?? 0;
        animateToCompletionInternal(orderIdKey, currentProgress);
        return;
      }

      startTime ??= timestamp - calculatedElapsed;

      const elapsed = timestamp - startTime;
      let newProgress = 0;

      if (elapsed < DURATION_PHASE_1) {
        const progressFraction = elapsed / DURATION_PHASE_1;
        newProgress = 90 * progressFraction;
      } else if (elapsed < totalDuration) {
        const elapsedInPhase2 = elapsed - DURATION_PHASE_1;
        const progressFraction = elapsedInPhase2 / DURATION_PHASE_2;
        newProgress = 90 + 9 * progressFraction;
      } else {
        newProgress = 99;
      }

      dispatchRef.current(updateDisplayProgressNew({orderIdKey, displayProgress: newProgress}));

      if (elapsed < totalDuration) {
        animationFramesRef.current[orderIdKey] = requestAnimationFrame(animate);
      } else {
        delete animationFramesRef.current[orderIdKey];
      }
    };

    animationFramesRef.current[orderIdKey] = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    currentlyProcessingOrderIdKeys.forEach((orderIdKey) => {
      const order = orderStateHashmapNew[orderIdKey];
      if (!order) return;

      if (order.state === DownloadStateTypes.DOWNLOADING) {
        startAnimation(orderIdKey, order.displayProgress ?? 0);
      } else if (order.state === DownloadStateTypes.READY) {
        if (!completingOrdersRef.current.has(orderIdKey)) {
          if (animationFramesRef.current[orderIdKey]) {
            cancelAnimationFrame(animationFramesRef.current[orderIdKey]);
            delete animationFramesRef.current[orderIdKey];
          }
          animatingOrdersRef.current.delete(orderIdKey);

          if ((order.displayProgress ?? 0) < 100) {
            animateToCompletionInternal(orderIdKey, order.displayProgress ?? 0);
          }
        }
      } else if (order.state === DownloadStateTypes.ERROR) {
        if (animationFramesRef.current[orderIdKey]) {
          cancelAnimationFrame(animationFramesRef.current[orderIdKey]);
          delete animationFramesRef.current[orderIdKey];
        }
        animatingOrdersRef.current.delete(orderIdKey);
        if ((order.displayProgress ?? 0) < 100) {
          dispatch(updateDisplayProgressNew({orderIdKey, displayProgress: 100}));
        }
      }
    });

    Object.keys(animationFramesRef.current).forEach((orderIdKey) => {
      if (!currentlyProcessingOrderIdKeys.includes(orderIdKey)) {
        cancelAnimationFrame(animationFramesRef.current[orderIdKey]);
        delete animationFramesRef.current[orderIdKey];
        animatingOrdersRef.current.delete(orderIdKey);
      }
    });
  }, [currentlyProcessingOrderIdKeys, orderStateHashmapNew, dispatch, startAnimation]);

  useEffect(() => {
    return () => {
      Object.values(animationFramesRef.current).forEach((frameId) => {
        cancelAnimationFrame(frameId);
      });
      animationFramesRef.current = {};
      animatingOrdersRef.current.clear();
    };
  }, []);
};
